import { HardDriveDownload, Trash2, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, IconButton } from "../components/Button.tsx";
import { formatBytes } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { removeSpot } from "./saver.ts";
import { idbStore } from "./store.ts";
import { offlineSupported, useSavedSpots } from "./useSavedSpots.ts";

/** Header button that opens the list of spots saved for offline use. */
export function SavedSpotsButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  if (!offlineSupported()) return null;
  return (
    <IconButton
      aria-label={t("offline.listTitle")}
      title={t("offline.listTitle")}
      icon={<HardDriveDownload aria-hidden="true" strokeWidth={1.75} />}
      onClick={onClick}
    />
  );
}

/** "Offline gespeichert": every saved spot with its size, open and delete. */
export function SavedSpotsPanel({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (slug: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { spots, loaded } = useSavedSpots();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const total = spots.reduce((n, s) => n + s.bytes, 0);

  return (
    <div className="saved-backdrop" onClick={onClose}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="saved-title"
        className="saved-panel ds-glass ds-glass--solid"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="saved-panel__head">
          <h2 id="saved-title" className="ds-text-title-2 m-0">
            {t("offline.listTitle")}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="ds-btn ds-btn--icon ds-btn--plain"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
          </button>
        </header>
        {loaded && spots.length === 0 ? (
          <p className="ds-text-body text-label-secondary m-0">{t("offline.empty")}</p>
        ) : (
          <ul className="ds-list saved-list" aria-label={t("offline.listTitle")}>
            {spots.map((s) => (
              <li key={s.slug} className="ds-row">
                <span className="ds-row__title">
                  <span className="ds-text-headline">{s.name}</span>
                  <span className="ds-text-subheadline text-label-secondary block">
                    {formatBytes(s.bytes, locale)}
                  </span>
                </span>
                <Button variant="tinted" onClick={() => onOpen(s.slug)}>
                  {t("offline.open")}
                </Button>
                <IconButton
                  tone="plain"
                  aria-label={t("offline.removeNamed", { name: s.name })}
                  icon={<Trash2 aria-hidden="true" strokeWidth={1.75} />}
                  onClick={() => void removeSpot(s.slug, { store: idbStore })}
                />
              </li>
            ))}
          </ul>
        )}
        {spots.length > 0 ? (
          <p className="ds-text-footnote text-label-secondary m-0">
            {t("offline.total", { count: spots.length, size: formatBytes(total, locale) })}
          </p>
        ) : null}
      </section>
    </div>
  );
}
